'use client';

// ПАПКИ — ГОРИЗОНТАЛЬНЫЕ СТРАНИЦЫ, КАК В TELEGRAM iOS (ТЗ ANIM-FIX О5 П6, претензия владельца
// 04.10.2026, видео: «переключение папок без хода и без свайпа»). Полоса папок «Ботов», «Чатов»
// и «Контактов» на телефоне в браузере:
//   • тап по папке — список уезжает вбок, новая папка въезжает с другой стороны, ходом
//     --m-dur/--m-ease; направление — по порядку папок в полосе;
//   • свайп влево/вправо по списку листает папки пальцем: текущая страница идёт за пальцем,
//     соседняя выезжает рядом; отпустили дальше 1/3 ширины или бросок — доводка к соседней,
//     иначе возврат. Горизонталь побеждает при |dx| > |dy|·1.2 (люфт 8 px) — вертикальная
//     прокрутка списка не ломается; горизонтальный ряд внутри списка, которому есть куда
//     прокрутиться в сторону пальца, жест забирает себе. У крайней папки жест не берётся —
//     внутри бокового слоя («Контакты») он уводит слой назад (useSideDrag).
// Данные и отбор по папке — как были (usePersistentData у хозяина списка): пейджер не
// рендерит списки сам. Ушедшая страница — снимок её разметки (неживой, без событий): он
// уезжает, пока на месте уже стоит живой список новой папки. Соседняя страница под пальцем —
// снимок этой папки с прошлого раза (рендер по требованию: текущая + та, что тянется); папку
// ещё не открывали — под пальцем пустое поле, живой список встаёт по доводке.
// Только телефон в браузере: в приложении папки листает натив, на широком экране — колонка.
//
// ПИЛЮЛЯ ПОЛОСЫ ЕДЕТ ВМЕСТЕ С ХОДОМ (ТЗ WEB-CAUSES М63, видео владельца 06.10 4c1f17b3): пейджер
// отдаёт полосе папок долю хода `p` — свайп каждым кадром по сдвигу пальца, тап и доводка тем же
// ходом --m-dur/--m-ease, что и страница (FoldersStrip, `StripDrive`). Образец — iOS
// `MsgsFoldersBar.setProgress(from, to, p)`, BotsScreen.swift `pagerMoved`.
// У КАЖДОЙ ПАПКИ СВОЯ ПРОКРУТКА (ТЗ WEB-CAUSES М64): документ один (канон SAFE-AREA-SHELL), поэтому
// пейджер помнит прокрутку каждой папки и при смене ставит документ на высоту новой папки:
// `target = max(min(curY, fadeDone), saved[to])` — общая у папок только зона ухода шапки (iOS
// `primeNeighbours`/`fadeDoneOffset`, BotsScreen.swift 1728–1742), дальше у каждой своя высота.

import { useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';

const SLOP = 8; // люфт до решения «чей жест»
const FLICK = 0.5; // px/мс: бросок листает и с малого хода
const MOVE = 'transform var(--m-dur) var(--m-ease)';

interface Pending {
  to: string;
  /** Высота прокрутки новой папки (М64): документ встаёт на неё по посадке. */
  target: number;
  dir: 1 | -1;
  /** Снимок ушедшей страницы и где он стоит сейчас (тап — 0, свайп — где отпустили). */
  ghost: HTMLElement;
  ghostX: number;
  /** Снимок соседней страницы под пальцем — снимается, когда на его месте встал живой список. */
  preview: HTMLElement | null;
  timer: number;
}

export interface FolderPager {
  /** Тап по папке в полосе: ход к ней. */
  pick: (id: string) => void;
  wrapRef: React.RefObject<HTMLDivElement | null>;
  pageRef: React.RefObject<HTMLDivElement | null>;
  /** Повесить жест на обёртку страницы; возвращает снятие. */
  bind: (wrap: HTMLElement | null) => (() => void) | undefined;
  /** Отложенный ход смены папки (страница смонтировалась позже смены). */
  run: () => void;
  /** Рука полосы папок (М63): полоса регистрирует сюда свою пилюлю, пейджер ведёт её долей хода. */
  strip: { current: StripDrive | null };
}

/** Пилюля полосы папок под управлением пейджера (ТЗ WEB-CAUSES М63; iOS MsgsFoldersBar.setProgress). */
export interface StripDrive {
  /** Пилюля между словами `from` → `to` на доле `p` (0…1) без хода; тон слов — той же долей.
   *  Пока полоса «держится», смена текущей папки сама пилюлю не двигает. */
  drag: (from: string, to: string, p: number) => void;
  /** Доводка к слову `to` ходом --m-dur (живая страница поехала на место тем же кадром). */
  go: (to: string) => void;
  /** Возврат к текущей папке ходом (недотянутый свайп, отказ отбора). */
  back: () => void;
}

/** Касания, которые взяли папки (жест решён в их пользу). Жест вешается на ФАЗУ ЗАХВАТА окна —
 *  он видит касание раньше бокового слоя, и тот (drawer.tsx, useSideDrag) по этой метке
 *  уступает: внутри слоя «Контактов» свайп вправо листает к прошлой папке, а с первой папки —
 *  уводит слой назад. */
const owned = new WeakSet<Event>();
export const takenByFolders = (e: Event): boolean => owned.has(e);

const phoneWeb = (): boolean =>
  typeof window !== 'undefined' &&
  window.innerWidth <= 820 &&
  !document.documentElement.classList.contains('app');

/** Неживой снимок страницы: без id (не двоить их в документе), недоступный и без событий. */
function snapshot(page: HTMLElement): HTMLElement {
  const g = page.cloneNode(true) as HTMLElement;
  g.removeAttribute('id');
  g.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  g.className = 'fpg-ghost';
  g.setAttribute('aria-hidden', 'true');
  g.setAttribute('inert', '');
  g.style.transition = 'none';
  g.style.transform = '';
  return g;
}

/** Ход страницы — ширина обрезающего поля (поле раздела `.content` / тело листа с их боковыми
 *  отступами), а не самой обёртки: страница обязана уйти за кромку экрана целиком. */
function travel(wrap: HTMLElement): number {
  const box = wrap.closest<HTMLElement>('.content, .dr-body');
  return box?.clientWidth || window.innerWidth;
}

/** Что прокручивается под страницей папки: на телефоне — документ (канон SAFE-AREA-SHELL), в
 *  листе — его прокручиваемое тело. */
function scroller(wrap: HTMLElement): HTMLElement | null {
  let el = wrap.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }
  return null;
}
const readY = (box: HTMLElement | null): number => (box ? box.scrollTop : window.scrollY);
function writeY(box: HTMLElement | null, y: number): void {
  if (box) box.scrollTo({ top: y, behavior: 'instant' });
  else window.scrollTo({ top: y, behavior: 'instant' });
}

/** ЗОНА УХОДА ШАПКИ (М64; iOS `fadeDoneOffset = plateDocTop + plateHeight + control − head`):
 *  прокрутка, на которой низ плашки поиска дошёл до верха строки кружков (линия кружков − --ctl).
 *  До неё папки делят одно состояние шапки, дальше у каждой своя высота. Плашки нет — 0. */
function fadeDone(wrap: HTMLElement, box: HTMLElement | null): number {
  const root = wrap.closest<HTMLElement>('.drawer') ?? wrap.closest<HTMLElement>('.main') ?? document.body;
  const plate = root.querySelector<HTMLElement>('.ai-bar-shell');
  const pr = plate?.getBoundingClientRect();
  if (!pr || pr.height === 0) return 0;
  const lead = root.querySelector<HTMLElement>('.lh-lead')?.getBoundingClientRect();
  const line = lead && lead.height > 0 ? lead.top : (box?.getBoundingClientRect().top ?? 0);
  return Math.max(0, Math.round(pr.bottom - line + readY(box)));
}

/** Вставить снимок в обёртку ровно по высоте живой страницы: у живой первый ребёнок отдаёт
 *  свой верхний отступ наружу (схлопывание полей), у снимка (абсолютный слой) — нет. */
function mount(wrap: HTMLElement, page: HTMLElement | null, el: HTMLElement): void {
  el.style.top = '0px';
  wrap.appendChild(el);
  const a = page?.firstElementChild?.getBoundingClientRect().top;
  const b = el.firstElementChild?.getBoundingClientRect().top;
  if (a !== undefined && b !== undefined) el.style.top = `${a - b}px`;
}

/** Ряд внутри списка, который сам прокрутится в сторону пальца (`fingerDx` > 0 — палец вправо,
 *  ряд показывает то, что слева): жест — ему. Ряд у своего края или с переполнением на пару
 *  пикселей (округление раскладки) жест не держит — листаются папки. */
function rowTakes(from: EventTarget | null, stop: HTMLElement, fingerDx: number): boolean {
  let el = from instanceof Element ? from : null;
  while (el && el !== stop) {
    if (el instanceof HTMLElement && el.scrollWidth - el.clientWidth > SLOP) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') {
        const room = fingerDx > 0 ? el.scrollLeft : el.scrollWidth - el.clientWidth - el.scrollLeft;
        if (room > 1) return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

/** Конец хода transform у элемента (или страховка). Возвращает отмену. */
function afterMove(el: HTMLElement, done: () => void): () => void {
  let over = false;
  const onEnd = (e: TransitionEvent): void => {
    if (e.target === el && e.propertyName === 'transform') finish();
  };
  const timer = window.setTimeout(() => finish(), 1000);
  function stop(): void {
    over = true;
    window.clearTimeout(timer);
    el.removeEventListener('transitionend', onEnd);
    el.removeEventListener('transitioncancel', onEnd);
  }
  function finish(): void {
    if (over) return;
    stop();
    done();
  }
  el.addEventListener('transitionend', onEnd);
  el.addEventListener('transitioncancel', onEnd);
  return stop;
}

export function useFolderPager(ids: readonly string[], current: string, onPick: (id: string) => void): FolderPager {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const st = useRef({
    ids: ids as readonly string[],
    cur: current,
    onPick,
    pending: null as Pending | null,
    /** Снимки страниц папок с прошлого раза — для соседа под пальцем. */
    shots: new Map<string, HTMLElement>(),
    /** Идущий ход: снять его следы (снимок, инлайн-стили) немедленно. */
    settle: null as (() => void) | null,
    /** Прокрутка каждой папки (М64): ключ — id папки, значение — высота документа/листа. */
    saved: new Map<string, number>(),
    /** Палец ведёт страницу — прокрутку текущей папки не перезаписываем. */
    dragging: false,
  });
  const strip = useRef<StripDrive | null>(null);
  // Свежие папки и рука отбора — к событиям (тап, свайп), без пересоздания жеста.
  useLayoutEffect(() => {
    st.current.ids = ids;
    st.current.onPick = onPick;
  });
  // Набор папок сменился (обновили список, другая учётка) — память прокрутки сбрасываем (М64).
  const idsKey = ids.join('\u0001');
  useEffect(() => {
    st.current.saved.clear();
  }, [idsKey]);
  // ПАМЯТЬ ПРОКРУТКИ ТЕКУЩЕЙ ПАПКИ (М64): на каждом scroll документа или листа страницы папки.
  // Ход смены и палец на странице — не записываем (страница в эти кадры чужая).
  useEffect(() => {
    const s = st.current;
    // Прокручиваемое место ищем один раз на обёртку (обход предков с getComputedStyle — не на
    // каждый кадр прокрутки).
    let seen: HTMLElement | null = null;
    let seenBox: HTMLElement | null = null;
    const box = (): HTMLElement | null => {
      const wrap = wrapRef.current;
      if (wrap !== seen) {
        seen = wrap;
        seenBox = wrap ? scroller(wrap) : null;
      }
      return seenBox;
    };
    const on = (e: Event): void => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const b = box();
      if (e.target !== (b ?? document)) return;
      if (s.pending || s.dragging || !phoneWeb()) return;
      s.saved.set(s.cur, readY(b));
    };
    window.addEventListener('scroll', on, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', on, { capture: true });
  }, []);

  /** Высота, на которой встанет папка `to` (М64): шапка общая до зоны её ухода, дальше — своя. */
  const targetOf = useCallback((to: string): number => {
    const s = st.current;
    const wrap = wrapRef.current;
    if (!wrap) return 0;
    const box = scroller(wrap);
    const cur = readY(box);
    s.saved.set(s.cur, cur);
    return Math.max(Math.min(cur, fadeDone(wrap, box)), s.saved.get(to) ?? 0);
  }, []);

  /** Довести всё идущее до конца сразу (новый тап посреди хода). */
  const flush = useCallback((): void => {
    const s = st.current;
    s.settle?.();
    s.settle = null;
    if (s.pending) {
      window.clearTimeout(s.pending.timer);
      s.pending.ghost.remove();
      s.pending.preview?.remove();
      s.pending = null;
      strip.current?.back();
    }
    const page = pageRef.current;
    if (page) {
      page.style.transition = '';
      page.style.transform = '';
    }
  }, []);

  /** Уходим со страницы `from` к `to`: снимок уходящей, отбор хозяину; ход — по смене папки. */
  const leave = useCallback(
    (to: string, dir: 1 | -1, ghostX: number, preview: HTMLElement | null, target: number): void => {
      const s = st.current;
      const page = pageRef.current;
      const wrap = wrapRef.current;
      if (!page || !wrap) {
        strip.current?.go(to);
        s.onPick(to);
        return;
      }
      s.saved.set(s.cur, readY(scroller(wrap)));
      const ghost = snapshot(page);
      s.shots.set(s.cur, ghost);
      ghost.style.transform = `translateX(${ghostX}px)`;
      mount(wrap, page, ghost);
      // Хозяин не сменил папку (отбор отказал) — снимок и сосед уходят, страница на место.
      const timer = window.setTimeout(() => {
        if (st.current.pending?.ghost === ghost) flush();
      }, 1000);
      s.pending = { to, target, dir, ghost, ghostX, preview, timer };
      s.onPick(to);
    },
    [flush],
  );

  const pick = useCallback(
    (id: string): void => {
      const s = st.current;
      if (id === s.cur || !phoneWeb()) {
        s.onPick(id);
        return;
      }
      flush();
      const dir: 1 | -1 = s.ids.indexOf(id) > s.ids.indexOf(s.cur) ? 1 : -1;
      // Пилюля держится на старом слове, пока не поедет страница (новая папка может грузиться).
      strip.current?.drag(s.cur, id, 0);
      leave(id, dir, 0, null, targetOf(id));
    },
    [flush, leave, targetOf],
  );

  // СМЕНА ПАПКИ ДОЕХАЛА ДО ЭКРАНА (живой список новой папки уже в странице): страница встаёт
  // туда, откуда приезжает новая папка, и едет на место; снимок ушедшей — в противоположную
  // сторону. Тем же кадром, до отрисовки. Страницы в этот миг нет (хозяин на время загрузки
  // папки рисует заглушку без неё) — ход ждёт её монтирования (resume из FolderPage).
  const run = useCallback((): void => {
    const s = st.current;
    const p = s.pending;
    const page = pageRef.current;
    const wrap = wrapRef.current;
    if (!p || p.to !== s.cur || !page || !wrap) return;
    s.pending = null;
    window.clearTimeout(p.timer);
    const w = travel(wrap);
    if (p.ghost.parentElement !== wrap) mount(wrap, page, p.ghost);
    // ПОСАДКА НА СВОЮ ВЫСОТУ (М64): документ встаёт на высоту новой папки без хода; снимок
    // ушедшей сдвигается на тот же шаг вниз и остаётся на экране там, где был.
    const box = scroller(wrap);
    const y0 = readY(box);
    if (Math.abs(p.target - y0) > 0.5) {
      writeY(box, p.target);
      const dy = readY(box) - y0;
      p.ghost.style.top = `${(parseFloat(p.ghost.style.top) || 0) + dy}px`;
    }
    page.style.transition = 'none';
    page.style.transform = `translateX(${p.ghostX + p.dir * w}px)`;
    p.preview?.remove();
    void page.offsetWidth;
    page.style.transition = MOVE;
    p.ghost.style.transition = MOVE;
    page.style.transform = 'translateX(0)';
    p.ghost.style.transform = `translateX(${p.ghostX - p.dir * w}px)`;
    // Пилюля — к новому слову тем же ходом и тем же кадром, что страница (М63).
    strip.current?.go(p.to);
    const ghost = p.ghost;
    const stop = afterMove(page, () => {
      s.settle = null;
      ghost.remove();
      page.style.transition = '';
      page.style.transform = '';
    });
    s.settle = () => {
      stop();
      ghost.remove();
    };
  }, []);

  useLayoutEffect(() => {
    const s = st.current;
    const was = s.cur;
    s.cur = current;
    if (was === current) return;
    const p = s.pending;
    if (!p || p.to !== current) {
      flush();
      return;
    }
    if (pageRef.current && wrapRef.current) run();
  }, [current, flush, run]);

  // СВАЙП ПО СПИСКУ — листать папки пальцем. Слушатели вешает сама страница (FolderPage) при
  // монтировании (хозяин может перемонтировать её — другая ветка разметки); касание ловится
  // на окне и отбирается по месту: поле раздела (.content) или тело листа (.dr-body).
  const bind = useCallback((wrap: HTMLElement | null): (() => void) | undefined => {
    if (!wrap || !('ontouchstart' in window)) return undefined;
    const area = wrap.closest<HTMLElement>('.content, .dr-body') ?? wrap;
    let phase: 'idle' | 'maybe' | 'drag' = 'idle';
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let lastX = 0;
    let lastT = 0;
    let v = 0;
    let w = 0;
    let nb: { id: string; dir: 1 | -1; el: HTMLElement; target: number } | null = null;
    let target: EventTarget | null = null;

    const neighbour = (dir: 1 | -1): { id: string; dir: 1 | -1; el: HTMLElement; target: number } | null => {
      const s = st.current;
      const id = s.ids[s.ids.indexOf(s.cur) + dir];
      if (id === undefined) return null;
      const shot = s.shots.get(id);
      const el = shot ?? document.createElement('div');
      if (!shot) {
        el.className = 'fpg-ghost';
        el.setAttribute('aria-hidden', 'true');
      }
      el.style.transition = 'none';
      el.style.transform = `translateX(${dir * travel(wrap)}px)`;
      mount(wrap, pageRef.current, el);
      // Сосед — на СВОЕЙ высоте (М64): снимок сдвинут на разницу текущей прокрутки и его посадки.
      const target = targetOf(id);
      const shift = readY(scroller(wrap)) - target;
      if (shift) el.style.top = `${(parseFloat(el.style.top) || 0) + shift}px`;
      return { id, dir, el, target };
    };
    const place = (x: number): void => {
      const page = pageRef.current;
      if (!page) return;
      page.style.transform = `translateX(${x}px)`;
      if (nb) {
        nb.el.style.transform = `translateX(${x + nb.dir * w}px)`;
        strip.current?.drag(st.current.cur, nb.id, w > 0 ? Math.min(1, Math.abs(x) / w) : 0);
      }
    };

    const onStart = (e: TouchEvent): void => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1 || phase === 'drag' || !phoneWeb()) return;
      if (st.current.ids.length < 2 || !st.current.ids.includes(st.current.cur)) return;
      // Скрытый раздел стопки вкладок (смонтирован, но не на экране) — не его жест.
      if (wrap.getClientRects().length === 0) return;
      // Жест ловит всё поле раздела: под коротким списком тоже страница папки (там палец
      // попадает в обёртку раздела — .app, body: поле кончилось раньше экрана, это предок
      // поля). Выше страницы (полоса папок, строка поиска) — не листаем.
      const tgt = e.target instanceof Node ? e.target : null;
      if (!tgt || !(area.contains(tgt) || tgt.contains(area))) return;
      if (!wrap.contains(tgt) && t.clientY < wrap.getBoundingClientRect().top) return;
      target = e.target;
      startX = lastX = t.clientX;
      startY = t.clientY;
      dx = 0;
      v = 0;
      lastT = performance.now();
      phase = 'maybe';
    };
    const onMove = (e: TouchEvent): void => {
      if (phase === 'idle') return;
      const t = e.touches[0];
      if (!t) return;
      const ddx = t.clientX - startX;
      const ddy = t.clientY - startY;
      if (phase === 'maybe') {
        if (Math.abs(ddx) < SLOP && Math.abs(ddy) < SLOP) return;
        if (Math.abs(ddx) <= Math.abs(ddy) * 1.2 || !e.cancelable || rowTakes(target, area, ddx)) {
          phase = 'idle';
          return;
        }
        const dir: 1 | -1 = ddx < 0 ? 1 : -1;
        flush();
        nb = neighbour(dir);
        // Крайняя папка: дальше листать некуда — жест отдаём (прокрутке и прочим).
        if (!nb) {
          phase = 'idle';
          return;
        }
        phase = 'drag';
        st.current.dragging = true;
        w = travel(wrap);
        const page = pageRef.current;
        if (page) page.style.transition = 'none';
        startX = t.clientX;
      }
      e.preventDefault();
      owned.add(e);
      const raw = t.clientX - startX;
      // Палец ведёт только в сторону соседа (назад за край — в ноль).
      dx = nb!.dir === 1 ? Math.min(0, Math.max(-w, raw)) : Math.max(0, Math.min(w, raw));
      const now = performance.now();
      if (now - lastT > 0) v = (t.clientX - lastX) / (now - lastT);
      lastX = t.clientX;
      lastT = now;
      place(dx);
    };
    const finish = (): void => {
      st.current.dragging = false;
      if (phase !== 'drag' || !nb) {
        phase = 'idle';
        return;
      }
      phase = 'idle';
      const cur = nb;
      nb = null;
      const toward = -cur.dir * v; // скорость в сторону соседа
      const go = toward > FLICK || (toward > -FLICK && Math.abs(dx) > w / 3);
      if (go) {
        // Сосед остаётся на месте, пока не встанет живой список; ушедшая — снимком там же.
        leave(cur.id, cur.dir, dx, cur.el, cur.target);
        return;
      }
      // Возврат: пилюля — обратно к текущему слову тем же ходом, что страница (М63).
      strip.current?.back();
      const page = pageRef.current;
      if (!page) {
        cur.el.remove();
        return;
      }
      page.style.transition = MOVE;
      cur.el.style.transition = MOVE;
      page.style.transform = 'translateX(0)';
      cur.el.style.transform = `translateX(${cur.dir * w}px)`;
      const stop = afterMove(page, () => {
        st.current.settle = null;
        cur.el.remove();
        page.style.transition = '';
        page.style.transform = '';
      });
      st.current.settle = () => {
        stop();
        cur.el.remove();
      };
    };

    window.addEventListener('touchstart', onStart, { passive: true, capture: true });
    window.addEventListener('touchmove', onMove, { passive: false, capture: true });
    window.addEventListener('touchend', finish, { passive: true, capture: true });
    window.addEventListener('touchcancel', finish, { passive: true, capture: true });
    return () => {
      window.removeEventListener('touchstart', onStart, { capture: true });
      window.removeEventListener('touchmove', onMove, { capture: true });
      window.removeEventListener('touchend', finish, { capture: true });
      window.removeEventListener('touchcancel', finish, { capture: true });
      nb?.el.remove();
    };
  }, [flush, leave, targetOf]);

  // Ушли со страницы (размонтирование) — снимки не держим.
  useEffect(() => {
    const s = st.current;
    return () => {
      if (s.pending) window.clearTimeout(s.pending.timer);
      s.shots.clear();
    };
  }, []);

  return { pick, wrapRef, pageRef, bind, run, strip };
}

/** Обёртка списка папки: страница, которую листает пейджер. `data-noswipe` — жест вкладок
 *  стопки (tab-stack) здесь не стартует: горизонталь принадлежит папкам. */
export function FolderPage({ pager, children }: { pager: FolderPager; children: ReactNode }) {
  const { bind, wrapRef, pageRef, run } = pager;
  useEffect(() => bind(wrapRef.current), [bind, wrapRef]);
  // Страница смонтирована заново (папка доехала после заглушки загрузки) — отложенный ход.
  useLayoutEffect(() => run(), [run]);
  return (
    <div className="fpg" ref={wrapRef} data-noswipe="">
      <div className="fpg-page" ref={pageRef}>
        {children}
      </div>
    </div>
  );
}
